import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { View, Text, Pressable, Platform, PanResponder } from 'react-native';
import * as Animatable from 'react-native-animatable';
import { FontAwesome5, Feather, AntDesign, MaterialIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import FlowTypeComponent from './FlowTypeComponent';
import styles from '@stylesheet';
import Measurable, { MeasurableHandle } from '@navigation/screens/MidLevel/UsersScreen/utils/MeasurableView';
import Animated, { runOnJS, useAnimatedProps, useAnimatedReaction, useSharedValue, withTiming } from 'react-native-reanimated';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { UserComponentProps } from '@navigation/screens/MidLevel/UsersScreen/Components/userComponent.native';
import { H2, Separator } from 'tamagui';
import { useDispatch } from 'react-redux';
import flowList from '../utils/flowList';
import { ADD_ACTION, REMOVE_ACTION } from '@reduxLocal/actions/types';
import { ReactNativeZoomableView, ReactNativeZoomableViewProps } from '@openspacelabs/react-native-zoomable-view';
import ArrowView from '@navigation/screens/MidLevel/UsersScreen/utils/ArrowView';
import { ActionArrow as StaticActionArrow } from './FlowComponent';
interface Action {
  icon: string;
  title: string;
  staticLink?: boolean;
  stage_action: any;
  key?: string;
  id?: string;
}

interface ActionListComponentProps {
  actionList: Action[];
  staticLink?: boolean;
  newActionList: Action[];
  item?: any;
  index?: number;
  pressCallback?: () => void;
  dragging: boolean;
}

interface ActionListItemProps {
  action: Action,
  index: number,
  zoomRef?: React.RefObject<ReactNativeZoomableView>,
  schemaData?: any,
  allItems?: any;
  showArrow?: boolean,
  transform?: { translateX: number, translateY: number },
}

interface ActionListHandle {
  setTranslate: (x: number, y: number) => void;
  setArrowTranslate: (path: string, color: string) => void;
  disableComponent: () => void;
  enableComponent: () => void;
  getValues: () => { translateX: number, translateY: number }
}

const ACTION_GAP = 70;

export const FlowListItem = forwardRef<ActionListHandle, ActionListItemProps & UserComponentProps>((props, ref) => {
  const { action, zoomProps, zoomRef, componentPositions, portrait, transform, profileData, index, showArrows, positionHandler, accelerateValue } = props;
  const [transformation, setTransformation] = useState({ translateX: transform?.translateX || 0, translateY: transform?.translateY || 0 })
  const [isPortrait, setIsPortrait] = useState(portrait);
  const [dragEnabled, setDragEnabled] = useState(false);
  const measureRef = useRef<MeasurableHandle>(null);
  const translationX = useSharedValue(0);
  const translationY = useSharedValue(0);
  const absoluteX = useSharedValue(0);
  const absoluteY = useSharedValue(0);
  const theme = themeSelector();
  const arrowPath = useSharedValue('M0,0 L0,0');
  const strokeColor = useSharedValue('rgb(90,192,234)');
  const snapThreshold = 50;
  const breakFreeThreshold = 50;
  const angle = useSharedValue(90);
  const ctx = useSharedValue({ startX: 0, startY: 0, startAbsoluteX: 0, startAbsoluteY: 0, isSnapped: false, snappedIndex: -1 });
  const positionValues = useSharedValue([]);
  const componentDimensions = { width: 350, height: 400 };
  const [open, setOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const dispatch = useDispatch();
  const [newActionList, setNewActionList] = useState(flowList);
  const [positions, setPositions] = useState<any[]>([]);
  const [isArrowDragged, setIsArrowDragged] = useState(false);
  const [hideArrows, setHideArrows] = useState(false);
  const snapValues = positions;

  useImperativeHandle(ref, () => ({
    setTranslate(x, y) {
      setTransformation({ translateX: x * accelerateValue, translateY: y * accelerateValue });
    },
    setArrowTranslate(path, color) {
      arrowPath.value = path;
      strokeColor.value = color;
    },
    disableComponent() {
      // setDragEnabled(false);
      setHideArrows(true);
    },
    enableComponent() {
      // setDragEnabled(true);
      setHideArrows(false);
    },
    getValues() {
      return transformation
    }
  }));

  const panResponder = PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onPanResponderGrant: (event, gestureState) => {
      setDragging(true);
      dispatch({ type: "STARTED DRAGGING ARROW" })
      setNewActionList(flowList);
      setOpen(false);
      const { locationX: viewX, locationY: viewY } = event.nativeEvent;
      const { zoomLevel } = zoomProps;
      ctx.value.startX = (viewX / zoomLevel) - (componentDimensions.width / 2);
      ctx.value.startY = -((isPortrait ? componentDimensions.height : 80) - (viewY / zoomLevel));
      ctx.value.startAbsoluteX = absoluteX.value;
      ctx.value.startAbsoluteY = absoluteY.value;
      ctx.value.isSnapped = false;
      ctx.value.snappedIndex = -1;
      const componentWidth = 300 + (ACTION_GAP * 2);
      const halfComponentWidth = componentWidth / 2;

      // Calculate the starting X position for the first component to be at -(componentWidth / 2)
      const startX = -((flowList.length * componentWidth) / 2);
      flowList.forEach((action, index) => {
        // Calculate the x position of each component
        const x = startX + (index * (componentWidth + ACTION_GAP)) + halfComponentWidth;

        positions.push({
          y: viewY + 280,  // Keeps the same vertical offset
          x: x,  // Set x to the calculated position
          plotX: x,  // Set plotX to the same value for consistent positioning
          plotY: viewY + 550,  // Keeps the same vertical position for plotY
          id: action.key,
          action: action.key
        });
      });
      ctx.value.startX = viewX;
      ctx.value.startY = viewY;
      ctx.value.startAbsoluteX = absoluteX.value;
      ctx.value.startAbsoluteY = absoluteY.value;
      ctx.value.isSnapped = false;
      ctx.value.snappedIndex = -1;
      setIsArrowDragged(true);
    },
    onPanResponderMove: (event, gestureState) => {
      const { locationX: viewX, locationY: viewY } = event.nativeEvent;
      const { dx: tX, dy: tY, moveX: ax, moveY: ay } = gestureState;
      const offsetX = zoomProps?.offsetX || 0;
      const offsetY = zoomProps?.offsetY || 0;
      const zoomLevel = zoomProps?.zoomLevel || 0.4;
      translationX.value = tX;
      translationY.value = tY;
      absoluteX.value = ax;
      absoluteY.value = ay;

      ctx.value.snappedIndex = -1;
      snapValues.forEach(({ x, y, plotX, plotY, id }, index) => {
        const adjustedSnapX = x * zoomLevel;
        const adjustedSnapY = y * zoomLevel;

        const xRep = adjustedSnapX - (viewX - offsetX);
        const yRep = adjustedSnapY - (viewY - offsetY);

        const positionIndex = positionValues.value.findIndex((pos) => pos.id === id);
        if (positionIndex !== -1) {
          positionValues.value[positionIndex].x = xRep;
          positionValues.value[positionIndex].y = yRep;
        } else {
          const position = { x: x, y: y, id };
          positionValues.value = [{ position }];
        }

        const distance = Math.sqrt(((plotX - (viewX / zoomLevel) + 20) ** 2) + ((plotY - (viewY / zoomLevel)) ** 2));

        if (distance < snapThreshold) {
          const interpolatedX = tX;
          const interpolatedY = tY;

          const controlPoint1X = 0;
          const minY = 80;
          const percentOfY = 0.1 * y;
          const controlPoint1Y = Math.max(minY, percentOfY) + distance * 0.1;
          const controlPoint2X = interpolatedX;
          const controlPoint2Y = interpolatedY - distance * 0.1;
          angle.value = 90;
          const arrowPathValue = `M0,${0} C${controlPoint1X},${controlPoint1Y} ${controlPoint2X},${controlPoint2Y} ${plotX - 200},${plotY - 260}`;
          arrowPath.value = arrowPathValue;
          ctx.value.snappedIndex = index;
          ctx.value.isSnapped = true;
        }
      });

      if (ctx.value.snappedIndex === -1) {
        const interpolatedX = tX;
        const interpolatedY = tY;

        const controlPoint1X = 0;
        const controlPoint1Y = ((interpolatedY / zoomLevel) + (tY * zoomLevel)) / 5;
        const controlPoint2X = viewX + offsetX - (tX * zoomLevel);
        const controlPoint2Y = viewY + offsetY - (tY * zoomLevel);

        const deltaX = interpolatedX - ctx.value.startX;
        const deltaY = interpolatedY - ctx.value.startY;
        const angleInRadians = Math.atan2(deltaY * 4, deltaX);
        const angleInDegrees = (angleInRadians * 180) / Math.PI;

        arrowPath.value = `M${0},${0} C${0},${((interpolatedY / zoomLevel) + (tY * zoomLevel)) / 5} ${(interpolatedX / zoomLevel)},${(interpolatedY / zoomLevel) - (tY * zoomLevel)} ${(interpolatedX / zoomLevel)},${(interpolatedY / zoomLevel)}`;
        angle.value = Math.atan2(interpolatedY, interpolatedX) * (180 / Math.PI);
        ctx.value.isSnapped = false;
      } else {
        const distanceFromSnapPoint = Math.sqrt(
          (snapValues[ctx.value.snappedIndex].plotX - (viewX / zoomLevel)) ** 2 +
          (snapValues[ctx.value.snappedIndex].plotY - (viewY / zoomLevel)) ** 2
        );
        if (distanceFromSnapPoint > breakFreeThreshold) {
          ctx.value.isSnapped = false;
        }
      }
    },
    onPanResponderRelease: () => {
      const snapId = snapValues[ctx.value.snappedIndex]?.id;
      if (snapId && snapId != null) {
        const action = newActionList.find(action => action.key === snapId);
        setDragging(false);
        action.component = null;
        dispatch(({ type: ADD_ACTION, payload: { actionData: { ...action } } }));
      } else {
        setDragging(false);
        setPositions([]);
        angle.value = 90;
        arrowPath.value = `M${0},${0} L${0},${0}`;
        ctx.value.isSnapped = false;
        ctx.value.snappedIndex = -1;
      }
      setIsArrowDragged(false);
    }
  });

  const dragPanResponder = PanResponder.create({
    onStartShouldSetPanResponder: () => true,
    onPanResponderGrant: (event, gestureState) => {
      setOpen(false);
      setDragEnabled(true);
    },
    onPanResponderMove: (event, gestureState) => {
      const { dx, dy } = gestureState;

      setTransformation(prev => {
        positionHandler?.({ x: prev.translateX + (dx / zoomProps.zoomLevel), y: prev.translateY + (dy / zoomProps.zoomLevel), id: index, width: 200, height: 200, fullView: isPortrait });
        return (
          { translateX: (prev.translateX + (dx / zoomProps.zoomLevel)), translateY: prev.translateY + (dy / zoomProps.zoomLevel) })
      });
      // positionHandler?.({ x: xTrans, y: yTrans, id: index, width: 200, height: 200, fullView: isPortrait });
      // const debouncedMeasure = debounce(() => measureRef.current?.measureLayout(), 0.1);
      // debouncedMeasure(); // Call the debounced function
    },
    onPanResponderRelease: () => {
      setDragEnabled(false);
      // measureRef.current?.measureLayout()
    }
  });

  useEffect(() => {
    positionHandler?.({ x: transformation.translateX, y: transformation.translateY, id: index, width: 200, height: 200, fullView: isPortrait });
  }, [measureRef])

  const measureView = useCallback((e) => {
    const { x, y, width, height } = e;
    // Adjust for zoom and offset
    if (x) {
      positionHandler?.({ x, y, id: index, width, height, fullView: isPortrait });
      // socket.emit('handleTranslate', { translateX: x, translateY: y, userId: profileDat?._id });
    }
  }, [measureRef, isPortrait]);

  return (
    <Animated.View style={[{ left: transformation.translateX, top: transformation.translateY, position: 'absolute', zIndex: dragEnabled ? 1000000 : 1 }]} >
      <Measurable style={{ borderWidth: 2, marginHorizontal: 20, position: 'absolute' }} ref={measureRef} commitMeasure={undefined} onMeasure={(x, y, width, height) => { measureView({ x, y, width, height }) }}>
        <Animatable.View useNativeDriver key={action.title + index} animation={'fadeIn'} delay={index * 100} style={{ padding: 10, width: 200, borderRadius: 20, elevation: 1, flexDirection: 'row', zIndex: 2, gap: 30, backgroundColor: theme ? "white" : "black", shadowRadius: 20, shadowOpacity: .2 }}>
          <MaterialIcons {...dragPanResponder.panHandlers} name="drag-indicator" size={45} style={{ zIndex: 10, left: 0, top: 35 }} color={theme ? "black" : "white"} />
          <Animatable.View transition={'minHeight'} style={{ flex: 10, minHeight: open ? 700 : 100 }}>
            <Animated.View style={{}} {...panResponder.panHandlers}>
              <LinearGradient colors={['#1E90FF', '#58BFEA']} key={index} style={{ paddingVertical: 20, minWidth: 600, flex: 1, padding: 15, gap: 20, borderRadius: 20, alignSelf: 'stretch' }}>
                <View style={{ flex: 1, gap: 20, flexDirection: 'row', flexWrap: 'wrap', maxHeight: 50 }}>
                  <FontAwesome5 style={{ textAlign: 'center', flex: 1 }} name={action?.icon} size={36} color="white" />
                  <H2 selectable={false} adjustsFontSizeToFit numberOfLines={2} style={{ fontWeight: "bold", flex: 5, textAlign: 'center', color: 'white', fontSize: 36, lineHeight: 45 }}>{action.title}</H2>
                  <Pressable onPress={() => {
                    if (Platform.OS === 'web') {
                      if (!open) {
                        zoomRef?.current?.zoomTo(1, { x: transformation.translateX * 2, y: transformation.translateY / 2 });
                      } else {
                        zoomRef.current!.zoomTo(0.5);
                      }
                    }
                    setOpen(!open);
                  }} style={[styles.centerEverything, { minWidth: 50 }]}>
                    <Feather name='settings' size={48} color={"white"} style={{ textAlign: 'center' }} />
                  </Pressable>
                </View>
                <Separator borderColor={"white"} />
                {
                  open && flowList.find(listAction => listAction.key === action.key)?.component?.({
                    index, initialVals: {
                      ...action,
                      action: null,
                      component: null
                    }
                  })
                }
              </LinearGradient>
            </Animated.View>
          </Animatable.View>
          {
            !dragEnabled &&
            <Pressable onPress={() => {
              dispatch({ type: REMOVE_ACTION, payload: { actionId: action.id, index: index } });
            }} style={[styles.centerEverything, { minWidth: 50, top: !open ? -50 : 0, right: -600 }]}>
              <Feather name='delete' size={48} color="red" style={{ textAlign: 'center' }} />
            </Pressable>
          }
          {
            !open && !dragEnabled &&
            <Animatable.View useNativeDriver transition={'marginTop'} style={[styles.centerEverything, { minHeight: 50, minWidth: 50, marginTop: !open ? 160 : 380, marginLeft: 180, overflow: "visible" }]}>
              <StaticActionArrow />
            </Animatable.View>
          }
        </Animatable.View>
      </Measurable>
    </Animated.View>
  )
});


const FlowListComponent: React.FC<ActionListComponentProps> = ({ staticLink, actionList, item, index, pressCallback, newActionList, dragging }) => {
  const [open, setOpen] = useState(false);
  const dispatch = useDispatch();

  return item ? (
    <Pressable
      style={{ width: 300 }}
      onPress={() => dispatch({
        type: ADD_ACTION,
        payload: {
          actionData: {
            ...item
          }
        }
      })}>
      <Animatable.View useNativeDriver key={item.title + index} animation={'fadeIn'} delay={index * 100} style={{ padding: 10, borderRadius: 20, elevation: 1, flexDirection: 'row', zIndex: 2, gap: 30 }}>
        <LinearGradient colors={['#1E90FF', '#58BFEA']} key={index} style={{ shadowRadius: 20, backgroundColor: "white", shadowOpacity: .2, flex: 1, padding: 15, gap: 20, borderRadius: 20, alignSelf: 'stretch' }}>
          <View style={{ gap: 10, flex: 1, flexDirection: 'row', flexWrap: 'wrap', maxHeight: 50 }}>
            <FontAwesome5 style={{ top: 5, textAlign: 'center', flex: 1 }} name={item.icon} size={24} color="white" />
            <Text selectable={false} adjustsFontSizeToFit numberOfLines={2} style={{ flex: 5, textAlign: 'center', color: 'white', fontSize: 20, lineHeight: 30 }}>{item.title}</Text>
          </View>
        </LinearGradient>
      </Animatable.View>
    </Pressable>
  ) : (
    <View style={{ alignSelf: 'center', minHeight: 120, position: 'absolute', flexDirection: 'row', top: 400, gap: ACTION_GAP }}>
      {actionList.map((action, index) => !staticLink ? (
        <FlowListItem action={action} index={index} />
      )
        :
        (
          <Animatable.View useNativeDriver key={action.title + index} animation={'fadeIn'} delay={index * 100} style={{ left: !dragging ? -170 : undefined, padding: 10, width: 400, borderRadius: 20, elevation: 1, flexDirection: 'row', zIndex: 2, gap: 30 }}>
            {
              !dragging &&
              <Pressable onPress={() => setOpen(!open)} style={[styles.centerEverything, { minWidth: 50 }]}>
                <Feather name='settings' size={48} color="white" style={{ textAlign: 'center' }} />
              </Pressable>
            }
            <LinearGradient colors={['#1E90FF', '#58BFEA']} key={index} style={{ minHeight: open ? 700 : undefined, minWidth: !dragging ? 600 : undefined, flex: 1, padding: 15, gap: 20, borderRadius: 20, alignSelf: 'stretch' }}>
              <View style={{ flex: 1, flexDirection: 'row', flexWrap: 'wrap', maxHeight: 50 }}>
                <FontAwesome5 style={{ textAlign: 'center', flex: 1 }} name={action.icon} size={!dragging ? 24 : 48} color="white" />
                <Text selectable={false} adjustsFontSizeToFit numberOfLines={2} style={{ flex: 5, textAlign: 'center', color: 'white', fontSize: 28, lineHeight: 30 }}>{action.title}</Text>
              </View>
              {
                !dragging && newActionList.length > 0 &&
                <View style={{ flex: 4 }}>
                  <FlowTypeComponent open={open} type={newActionList[0].key} />
                </View>
              }
            </LinearGradient>

          </Animatable.View>
        )
      )}
    </View>
  );
}

export default memo(FlowListComponent);